import { useEffect, useRef, useState } from "react";
import {
  Alert, AlertDescription, AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  AlertIcon, Badge, Box, Button, Collapse, Flex, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, SimpleGrid, Spinner, Tbody, Td, Text, Textarea, Th, Thead, Tr, useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import {
  deleteFiscalDraft, downloadFiscalFile, FiscalFileKind, FiscalInvoice, FiscalInvoiceResult, getFiscalInvoice, issueFiscalInvoice,
  refreshFiscalInvoice, saveFiscalDraft,
} from "../../../services/fiscalService";
import { CancelInvoiceModal, CorrectionLetterModal, InvoiceEmailModal } from "./FiscalInvoiceActions";
import { crm } from "../../../theme";
import { brl } from "./duimp";
import { formatNcm, TableCard, tdProps, thProps } from "./tableStyles";
import { statusInfo } from "./fiscalLabels";

const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">{children}</FormLabel>
);

const eventLabel: Record<string, string> = {
  issue: "Envio", consult: "Consulta", cancel: "Cancelamento", correction_letter: "Carta de correção", email: "E-mail",
};

/** Detalhe da NF-e: confere itens e valores, ajusta textos do rascunho, emite, consulta e baixa DANFE/XML. */
const FiscalInvoiceModal = ({ invoiceId, onClose, onChanged, onShipment, onChildren, onSale, onEditSale }: {
  invoiceId: string;
  onClose: () => void;
  onChanged: () => void;
  /** Entrada por conta e ordem autorizada → gerar remessa (NF mãe). */
  onShipment?: (entryId: string) => void;
  /** Remessa (NF mãe) autorizada → gerar filhotes. */
  onChildren?: (motherId: string) => void;
  /** Entrada de importação autorizada → venda encomenda. */
  onSale?: (entryId: string) => void;
  /** Venda (rascunho/recusada) → reabrir a tela de venda e recalcular. */
  onEditSale?: (invoiceId: string) => void;
}) => {
  const toast = useToast();
  const [nf, setNf] = useState<FiscalInvoice | null>(null);
  const [saved, setSaved] = useState<string>("");
  const [busy, setBusy] = useState("");
  const [action, setAction] = useState<"" | "cancel" | "correction" | "email">("");
  const [confirm, setConfirm] = useState<"" | "issue" | "delete" | "discard">("");
  const [showEvents, setShowEvents] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const apply = (invoice: FiscalInvoice) => { setNf(invoice); setSaved(JSON.stringify(invoice)); };
  useEffect(() => {
    getFiscalInvoice(invoiceId).then(apply).catch(() => toast({ status: "error", title: "Não foi possível abrir a nota" }));
  }, [invoiceId]);

  const dirty = !!nf && JSON.stringify(nf) !== saved;
  const editable = !!nf?.canEdit;
  const patchItem = (index: number, field: "productCode" | "description" | "unit", value: string) =>
    setNf((n) => n && { ...n, items: n.items.map((it, i) => (i === index ? { ...it, [field]: value } : it)) });

  const handle = (result: FiscalInvoiceResult, okTitle: string) => {
    if (result.invoice) apply(result.invoice);
    setErrors(result.success ? [] : result.errors);
    if (!result.success) {
      toast({ status: "error", title: "Não deu certo", description: result.errors.join("\n"), duration: 10000, isClosable: true });
      return false;
    }
    toast({ status: "success", title: result.errors.length ? result.errors.join(" ") : okTitle });
    onChanged();
    return true;
  };

  const save = async () => {
    if (!nf) return false;
    setBusy("save");
    const ok = handle(await saveFiscalDraft(nf.id, nf), "Rascunho salvo");
    setBusy("");
    return ok;
  };

  const issue = async () => {
    if (!nf) return;
    setConfirm("");
    if (dirty && !(await save())) return;
    setBusy("issue");
    const result = await issueFiscalInvoice(nf.id);
    setBusy("");
    handle(result, result.invoice?.status === "authorized" ? "NF-e autorizada" : "NF-e enviada; aguardando a SEFAZ");
  };

  const refresh = async () => {
    if (!nf) return;
    setBusy("refresh");
    handle(await refreshFiscalInvoice(nf.id), "Status atualizado");
    setBusy("");
  };

  const remove = async () => {
    if (!nf) return;
    setConfirm("");
    setBusy("delete");
    const result = await deleteFiscalDraft(nf.id);
    setBusy("");
    if (!result.success) {
      toast({ status: "error", title: "Não foi possível excluir", description: result.errors.join("\n") });
      return;
    }
    toast({ status: "success", title: "Rascunho excluído" });
    onChanged();
    onClose();
  };

  const download = async (kind: FiscalFileKind, seq?: number) => {
    if (!nf) return;
    setBusy(seq ? `${kind}-${seq}` : kind);
    try { await downloadFiscalFile(nf.id, kind, seq); }
    catch { toast({ status: "error", title: "Não foi possível baixar o arquivo" }); }
    finally { setBusy(""); }
  };

  /** Retorno de cancelar/CC-e/e-mail: atualiza a nota (ou recarrega, se o servidor não mandou). */
  const actionDone = (okTitle?: string) => async (result: FiscalInvoiceResult) => {
    if (result.invoice) apply(result.invoice);
    else if (nf) getFiscalInvoice(nf.id).then(apply).catch(() => {});
    if (result.success && okTitle) toast({ status: result.errors.length ? "warning" : "success", title: result.errors.length ? result.errors.join(" ") : okTitle });
    onChanged();
  };

  const close = () => (dirty ? setConfirm("discard") : onClose());
  const status = nf ? statusInfo[nf.status] : null;

  return (
    <Modal isOpen onClose={close} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="17px">
          <Flex align="center" gap="10px" wrap="wrap">
            <Text>{nf?.number ? `NF-e nº ${nf.number}` : "NF-e (rascunho)"}</Text>
            {status && <Badge colorScheme={status.color}>{status.label}</Badge>}
            {(nf?.environment ?? nf?.issuerEnvironment) === "homologacao" && <Badge colorScheme="purple">Homologação · sem valor fiscal</Badge>}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!nf ? (
            <Flex justify="center" py="32px"><Spinner /></Flex>
          ) : (
            <Box>
              {(errors.length > 0 || nf.lastErrors) && (
                <Alert status={errors.length === 0 && nf.status === "processing" ? "warning" : "error"} borderRadius="10px" mb="12px" alignItems="flex-start">
                  <AlertIcon />
                  <AlertDescription fontSize="13px" whiteSpace="pre-line">{errors.length > 0 ? errors.join("\n") : nf.lastErrors}</AlertDescription>
                </Alert>
              )}

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px" fontSize="13px" mb="14px">
                <Box><Text color={crm.textMuted}>Emitente</Text><Text fontWeight="600">{nf.issuerName} · {nf.issuerCnpj}</Text></Box>
                <Box><Text color={crm.textMuted}>Destinatário</Text><Text fontWeight="600">{nf.recipientName} {nf.recipientCountry ? `· ${nf.recipientCountry}` : ""}</Text></Box>
                <Box><Text color={crm.textMuted}>Natureza / processo</Text><Text fontWeight="600">{nf.natureOperation} {nf.processRef ? `· ${nf.processRef}` : ""}</Text></Box>
                {nf.carrierName && <Box><Text color={crm.textMuted}>Transportadora</Text><Text fontWeight="600">{nf.carrierName}</Text></Box>}
                {nf.container && <Box><Text color={crm.textMuted}>Contêiner</Text><Text fontWeight="600">{nf.container}</Text></Box>}
                {nf.customerOrder && <Box><Text color={crm.textMuted}>Pedido do cliente</Text><Text fontWeight="600">{nf.customerOrder}</Text></Box>}
                {(nf.installments?.length ?? 0) > 0 && (
                  <Box gridColumn={{ md: "span 2" }}>
                    <Text color={crm.textMuted}>Parcelas</Text>
                    <Text fontWeight="600">
                      {nf.installments.map((x: any) => `${x.number} · ${DateTime.fromISO(x.dueDate, { setZone: true }).toFormat("dd/LL/yyyy")} · ${brl(x.value)}`).join("  |  ")}
                    </Text>
                  </Box>
                )}
                {nf.accessKey && <Box gridColumn={{ md: "span 3" }}><Text color={crm.textMuted}>Chave de acesso</Text><Text fontWeight="600" fontFamily="mono">{nf.accessKey}</Text></Box>}
              </SimpleGrid>

              {nf.status === "authorized" && nf.cancelRequestedAt && (
                <Alert status="warning" borderRadius="10px" mb="12px" alignItems="flex-start">
                  <AlertIcon />
                  <AlertDescription fontSize="13px">
                    Cancelamento pedido em {DateTime.fromISO(nf.cancelRequestedAt).toFormat("dd/LL/yyyy HH:mm")} sem resposta da Focus.
                    Clique em Atualizar status para confirmar; até lá nenhuma nota sai desta.
                  </AlertDescription>
                </Alert>
              )}
              {nf.status === "cancelled" && (
                <Alert status="warning" borderRadius="10px" mb="12px" alignItems="flex-start">
                  <AlertIcon />
                  <AlertDescription fontSize="13px">
                    Cancelada{nf.cancelledAt ? ` em ${DateTime.fromISO(nf.cancelledAt).toFormat("dd/LL/yyyy HH:mm")}` : ""}
                    {nf.cancellationReason ? ` · ${nf.cancellationReason}` : ""}
                  </AlertDescription>
                </Alert>
              )}

              {(nf.correctionLetters?.length ?? 0) > 0 && (
                <Box mb="14px">
                  <Text fontSize="13px" fontWeight="700" mb="6px">Cartas de correção (vale a mais recente)</Text>
                  <TableCard>
                    <Thead>
                      <Tr>
                        <Th {...thProps}>Nº</Th>
                        <Th {...thProps}>Correção</Th>
                        <Th {...thProps}>Quando / quem</Th>
                        <Th {...thProps}></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {nf.correctionLetters!.map((c) => (
                        <Tr key={c.sequence}>
                          <Td {...tdProps} fontWeight="600">{c.sequence}</Td>
                          <Td {...tdProps} whiteSpace="pre-wrap" minW="300px">{c.text}</Td>
                          <Td {...tdProps} whiteSpace="nowrap" color={crm.textMuted}>
                            {DateTime.fromISO(c.createdDate).toFormat("dd/LL/yyyy HH:mm")} · {c.userName ?? "—"}
                          </Td>
                          <Td {...tdProps} whiteSpace="nowrap">
                            {c.hasPdf && <Button size="xs" variant="outline" mr="6px" isLoading={busy === `cce-pdf-${c.sequence}`} onClick={() => download("cce-pdf", c.sequence)}>PDF</Button>}
                            {c.hasXml && <Button size="xs" variant="outline" isLoading={busy === `cce-xml-${c.sequence}`} onClick={() => download("cce-xml", c.sequence)}>XML</Button>}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </TableCard>
                </Box>
              )}

              <TableCard>
                <Thead>
                  <Tr>
                    <Th {...thProps}>#</Th>
                    <Th {...thProps}>Código</Th>
                    <Th {...thProps}>Descrição</Th>
                    <Th {...thProps}>NCM / CFOP</Th>
                    <Th {...thProps}>Un.</Th>
                    <Th {...thProps} isNumeric>Qtd.</Th>
                    <Th {...thProps} isNumeric>Valor produto</Th>
                    <Th {...thProps} isNumeric>Outras desp.</Th>
                    <Th {...thProps} isNumeric>ICMS</Th>
                    <Th {...thProps} isNumeric>II</Th>
                    <Th {...thProps} isNumeric>IPI</Th>
                    <Th {...thProps} isNumeric>PIS / COFINS</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {nf.items.map((it, index) => (
                    <Tr key={index}>
                      <Td {...tdProps}>{it.itemNumber}</Td>
                      <Td {...tdProps} minW="150px">
                        {editable ? <Input size="xs" maxLength={60} value={it.productCode} onChange={(e) => patchItem(index, "productCode", e.target.value)} /> : it.productCode}
                      </Td>
                      <Td {...tdProps} minW="240px">
                        {editable ? <Input size="xs" maxLength={120} value={it.description} onChange={(e) => patchItem(index, "description", e.target.value)} /> : it.description}
                      </Td>
                      <Td {...tdProps} whiteSpace="nowrap">{formatNcm(it.ncm)} · {it.cfop}</Td>
                      <Td {...tdProps} minW="70px">
                        {editable ? <Input size="xs" maxLength={6} value={it.unit} onChange={(e) => patchItem(index, "unit", e.target.value)} /> : it.unit}
                      </Td>
                      <Td {...tdProps} isNumeric>{it.quantity.toLocaleString("pt-BR")}</Td>
                      <Td {...tdProps} isNumeric>{brl(it.grossValue)}</Td>
                      <Td {...tdProps} isNumeric>{brl(it.otherExpenses)}</Td>
                      <Td {...tdProps} isNumeric whiteSpace="nowrap">{it.taxes.icmsCst ? `${it.taxes.icmsCst} · ` : ""}{brl(it.taxes.icmsValue ?? 0)}</Td>
                      <Td {...tdProps} isNumeric>{brl(it.taxes.iiValue ?? 0)}</Td>
                      <Td {...tdProps} isNumeric>{brl(it.taxes.ipiValue ?? 0)}</Td>
                      <Td {...tdProps} isNumeric whiteSpace="nowrap">{brl(it.taxes.pisValue ?? 0)} / {brl(it.taxes.cofinsValue ?? 0)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </TableCard>

              <Flex mt="12px" gap="16px" wrap="wrap" justify="flex-end" fontSize="13px" color={crm.textMuted}>
                <Text>Produtos: <b>{brl(nf.totals.products)}</b></Text>
                {nf.totals.freight + nf.totals.insurance > 0 && <Text>Frete/seguro: <b>{brl(nf.totals.freight + nf.totals.insurance)}</b></Text>}
                {nf.totals.discount > 0 && <Text>Desconto: <b>{brl(nf.totals.discount)}</b></Text>}
                <Text>Outras despesas: <b>{brl(nf.totals.otherExpenses)}</b></Text>
                {(nf.totals.icms ?? 0) > 0 && <Text>ICMS: <b>{brl(nf.totals.icms)}</b></Text>}
                {nf.totals.icmsSt > 0 && <Text>ICMS ST: <b>{brl(nf.totals.icmsSt + (nf.totals.fcpSt ?? 0))}</b></Text>}
                <Text>II: <b>{brl(nf.totals.ii)}</b></Text>
                <Text>IPI: <b>{brl(nf.totals.ipi)}</b></Text>
                <Text>PIS: <b>{brl(nf.totals.pis)}</b></Text>
                <Text>COFINS: <b>{brl(nf.totals.cofins)}</b></Text>
                <Text color={crm.text} fontSize="15px">Total da nota: <b>{brl(nf.totals.total)}</b></Text>
              </Flex>

              <FormControl mt="14px">
                <Label>Informações ao fisco</Label>
                {editable
                  ? <Textarea size="sm" rows={3} maxLength={2000} value={nf.fiscoInfo ?? ""} onChange={(e) => setNf({ ...nf, fiscoInfo: e.target.value })} />
                  : <Text fontSize="13px" whiteSpace="pre-line">{nf.fiscoInfo || "—"}</Text>}
              </FormControl>

              <Button variant="link" size="sm" mt="14px" onClick={() => setShowEvents((v) => !v)}>
                {showEvents ? "Ocultar" : "Ver"} histórico ({nf.events.length})
              </Button>
              <Collapse in={showEvents}>
                <Box mt="8px" fontSize="12px">
                  {nf.events.map((ev, i) => (
                    <Box key={i} borderBottom={`1px solid ${crm.border}`} py="6px">
                      <Text fontWeight="600">
                        {eventLabel[ev.type] ?? ev.type} · {DateTime.fromISO(ev.createdDate).toFormat("dd/LL/yyyy HH:mm")} · {ev.userName ?? "—"} · HTTP {ev.httpStatus ?? "—"}
                      </Text>
                      <Text color={crm.textMuted} whiteSpace="pre-wrap" noOfLines={4}>{ev.response}</Text>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap="8px" flexWrap="wrap">
          {nf?.status === "draft" && (
            <Button variant="ghost" colorScheme="red" mr="auto" isLoading={busy === "delete"} onClick={() => setConfirm("delete")}>Excluir rascunho</Button>
          )}
          {nf?.status === "authorized" && nf.canManage && (
            <Button variant="ghost" colorScheme="red" mr="auto" onClick={() => setAction("cancel")}>Cancelar NF-e</Button>
          )}
          {nf?.status === "authorized" && nf.canManage && (
            <Button variant="outline" onClick={() => setAction("correction")}>Carta de correção</Button>
          )}
          {nf && ["authorized", "cancelled"].includes(nf.status) && nf.canManage && (
            <Button variant="outline" onClick={() => setAction("email")}>Enviar por e-mail</Button>
          )}
          {nf?.status === "authorized" && nf.operation === "import_entry_on_behalf" && onShipment && (
            <Button variant="outline" onClick={() => onShipment(nf.id)}>Gerar remessa</Button>
          )}
          {nf?.status === "authorized" && nf.operation === "on_behalf_shipment" && onChildren && (
            <Button variant="outline" onClick={() => onChildren(nf.id)}>Gerar filhotes</Button>
          )}
          {nf?.status === "authorized" && nf.operation === "import_entry" && onSale && (
            <Button variant="outline" onClick={() => onSale(nf.id)}>Gerar venda</Button>
          )}
          {editable && nf?.hasSaleRequest && onEditSale && (
            <Button variant="outline" isDisabled={dirty} title={dirty ? "Salve ou descarte as alterações antes" : undefined}
              onClick={() => onEditSale(nf.id)}>Editar venda</Button>
          )}
          {nf?.hasDanfe && <Button variant="outline" isLoading={busy === "danfe"} onClick={() => download("danfe")}>DANFE</Button>}
          {nf?.hasXml && <Button variant="outline" isLoading={busy === "xml"} onClick={() => download("xml")}>XML</Button>}
          {nf?.hasCancellationXml && (
            <Button variant="outline" isLoading={busy === "cancel-xml"} onClick={() => download("cancel-xml")}>XML cancelamento</Button>
          )}
          {nf && ["processing", "rejected", "authorized"].includes(nf.status) && (
            <Button variant="outline" isLoading={busy === "refresh"} onClick={refresh}>Atualizar status</Button>
          )}
          {editable && <Button variant="outline" isDisabled={!dirty} isLoading={busy === "save"} onClick={save}>Salvar</Button>}
          {editable && (
            <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isLoading={busy === "issue"} onClick={() => setConfirm("issue")}>
              {nf?.status === "rejected" ? "Reenviar" : "Emitir NF-e"}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>

      {nf && action === "cancel" && <CancelInvoiceModal nf={nf} onClose={() => setAction("")} onDone={actionDone("NF-e cancelada")} />}
      {nf && action === "correction" && <CorrectionLetterModal nf={nf} onClose={() => setAction("")} onDone={actionDone("Carta de correção registrada")} />}
      {nf && action === "email" && <InvoiceEmailModal nf={nf} onClose={() => setAction("")} onDone={actionDone()} />}

      <AlertDialog isOpen={!!confirm} leastDestructiveRef={cancelRef} onClose={() => setConfirm("")}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="16px">
              {confirm === "issue" ? "Emitir NF-e?" : confirm === "delete" ? "Excluir rascunho?" : "Descartar alterações?"}
            </AlertDialogHeader>
            <AlertDialogBody fontSize="14px">
              {confirm === "issue" && (nf?.issuerEnvironment === "producao"
                ? `A nota vai para a SEFAZ em PRODUÇÃO, com valor fiscal. Total ${brl(nf?.totals.total ?? 0)}.`
                : `A nota vai para a SEFAZ em homologação (teste, sem valor fiscal). Total ${brl(nf?.totals.total ?? 0)}.`)}
              {confirm === "delete" && "O rascunho some da lista. Nada foi enviado à SEFAZ."}
              {confirm === "discard" && "As alterações deste rascunho ainda não foram salvas."}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirm("")}>Voltar</Button>
              {confirm === "issue" && <Button bg={crm.brand} color="white" ml={3} onClick={issue}>Emitir</Button>}
              {confirm === "delete" && <Button colorScheme="red" ml={3} onClick={remove}>Excluir</Button>}
              {confirm === "discard" && <Button colorScheme="red" ml={3} onClick={() => { setConfirm(""); onClose(); }}>Descartar</Button>}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
};

export default FiscalInvoiceModal;
